import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, cardTitle, tbl, th, td, btnPrimary } from '../lib/theme'
import * as XLSX from 'xlsx'

const fmt = (n) => '₦' + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits:0 })

function rag(pct) {
  if (pct >= 100) return { color:'#3fb950', bg:'rgba(63,185,80,.15)',  border:'rgba(63,185,80,.3)',  label:'Met'   }
  if (pct >= 70)  return { color:'#E8A020', bg:'rgba(232,160,32,.15)', border:'rgba(232,160,32,.3)', label:'Near'  }
  return              { color:'#f85149', bg:'rgba(248,81,73,.12)',  border:'rgba(248,81,73,.25)', label:'Below' }
}


const REPORT_TYPES = [
  { value:'super-agent',  label:'By Super Agent'          },
  { value:'weekly',       label:'Weekly Agent Detail'     },
  { value:'lga',          label:'By LGA Summary'          },
  { value:'funding',      label:'Wallet Funding Log'      },
  { value:'infractions',  label:'Weekly Infractions Log'  },
]

export default function Reports({ session, roleCtx }) {
  const [saScores,    setSaScores]    = useState([])
  const [agents,      setAgents]      = useState([])
  const [funding,     setFunding]     = useState([])
  const [superAgents, setSuperAgents] = useState([])
  const [lgas,        setLgas]        = useState([])
  const [targets,     setTargets]     = useState({})
  const [season,      setSeason]      = useState('dry')
  const [infractions, setInfractions] = useState([])
  const [loading,     setLoading]     = useState(true)

  const [reportType, setReportType] = useState('super-agent')
  const [filterSA,   setFilterSA]   = useState('')
  const [filterLga,  setFilterLga]  = useState('')
  const [dateFrom,   setDateFrom]   = useState('')
  const [dateTo,     setDateTo]     = useState('')

  const selS = {
    fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'8px 12px',
    color:T.txtPrimary, cursor:'pointer',
  }

  useEffect(() => {
    async function load() {
      const [
        { data:sc }, { data:ag }, { data:wf },
        { data:sa }, { data:lg }, { data:tg },
        { data:se }, { data:inf },
      ] = await Promise.all([
        supabase.from('super_agent_scores').select('*, super_agents(company_name,lgas(name,zone))').order('week_ending',{ascending:false}).limit(2000),
        supabase.from('agents').select('id,agent_id,full_name,agent_class,lga_id,super_agent_id,lgas(name,zone,tier),super_agents(company_name)').order('full_name'),
        supabase.from('wallet_funding').select('*').order('week_ending',{ascending:false}).limit(5000),
        supabase.from('super_agents').select('id,company_name,lgas(name)').order('company_name'),
        supabase.from('lgas').select('id,name').order('name'),
        supabase.from('weekly_targets').select('*').order('effective_from',{ascending:false}).limit(10),
        supabase.from('app_settings').select('*'),
        supabase.from('infractions').select('*, super_agents(company_name,owner_name,lgas(name))').order('date_logged',{ascending:false}).limit(500),
      ])
      setSaScores(sc||[]); setAgents(ag||[]); setFunding(wf||[])
      setSuperAgents(sa||[]); setLgas(lg||[])
      const tm={}; (tg||[]).forEach(t=>{ if(!t.tier) return; if(!tm[t.tier]) tm[t.tier]={}; if(!tm[t.tier][t.agent_class]) tm[t.tier][t.agent_class]=t.dry_season_target }); setTargets(tm)
      if(se){const s=se.find(x=>x.key==='season');if(s)setSeason(s.value)}
      setInfractions(inf||[])
      setLoading(false)
    }
    load()
  }, [])

  const seasonFactor = season === 'rain' ? 0.75 : 1.0

  function filterByDate(rows, dateField) {
    return rows.filter(r =>
      (!dateFrom || r[dateField] >= dateFrom) &&
      (!dateTo   || r[dateField] <= dateTo)
    )
  }

  const filteredSA = filterByDate(saScores, 'week_ending').filter(r =>
    (!filterSA  || filterSA === '__all__' ? true : r.super_agent_id === filterSA) &&
    (!filterLga || r.super_agents?.lgas?.name === lgas.find(l => l.id === filterLga)?.name)
  )

  const filteredFunding = filterByDate(funding, 'week_ending')

  const filteredInfractions = filterByDate(infractions, 'date_logged').filter(r =>
    (!filterSA || r.super_agent_id === filterSA) &&
    (!filterLga || r.super_agents?.lgas?.name === lgas.find(l => l.id === filterLga)?.name)
  )

  // Agent-level board for weekly report
  const agentBoard = (() => {
    const agMap = {}
    filteredFunding.forEach(r => {
      const key = r.agent_id || r.agent_id_text; if (!key) return
      agMap[key] = (agMap[key] || 0) + (r.amount_funded || r.amount || 0)
    })
    return agents
      .filter(a =>
        (!filterSA  || a.super_agent_id === filterSA) &&
        (!filterLga || a.lga_id === filterLga)
      )
      .map(a => {
        const agTier     = a.lgas?.tier || 'A'
        const baseTarget = (targets[agTier]?.[a.agent_class] || targets['A']?.[a.agent_class] || 120000) * seasonFactor
        const funded     = agMap[a.id] || agMap[a.agent_id] || 0
        const pct        = baseTarget > 0 ? Math.round(funded / baseTarget * 100) : 0
        return { ...a, baseTarget, funded, pct, met: funded >= baseTarget }
      })
  })()

  // LGA summary
  const lgaSummary = {}
  filteredSA.forEach(r => {
    const lga = r.super_agents?.lgas?.name || 'Unknown'
    if (!lgaSummary[lga]) lgaSummary[lga] = { count:0, totalFunded:0, totalTarget:0, met:0 }
    lgaSummary[lga].count++
    lgaSummary[lga].totalFunded += r.total_funded || 0
    lgaSummary[lga].totalTarget += r.weekly_target || 0
    if (r.target_met) lgaSummary[lga].met++
  })

  function exportExcel() {
    let rows = [], sheetName = 'Report'
    if (reportType === 'super-agent') {
      sheetName = 'Super Agent Report'
      rows = filteredSA.map(r => ({
        'Week Ending': r.week_ending, 'Company Name': r.super_agents?.company_name,
        'LGA': r.super_agents?.lgas?.name, 'Agents': r.agent_count,
        'Total Funded': r.total_funded, 'Weekly Target': r.weekly_target,
        '% of Target': r.funding_pct + '%', 'Status': r.funding_pct>=100?'Met':r.funding_pct>=70?'Near':'Below',
      }))
    } else if (reportType === 'weekly') {
      sheetName = 'Agent Weekly Report'
      rows = agentBoard.map(r => ({
        'Agent ID': r.agent_id||'', 'Agent Name': r.full_name, 'LGA': r.lgas?.name,
        'Company Name': r.super_agents?.company_name||'—', 'Class': r.agent_class,
        'Weekly Target': r.baseTarget, 'Total Funded': r.funded,
        '% of Target': r.pct + '%', 'Status': r.pct>=100?'Met':r.pct>=70?'Near':'Below',
      }))
    } else if (reportType === 'lga') {
      sheetName = 'LGA Summary'
      rows = Object.entries(lgaSummary).map(([lga, d]) => ({
        'LGA': lga, 'Super Agents': d.count, 'Total Funded': d.totalFunded,
        'Total Target': d.totalTarget,
        '% of Target': d.totalTarget > 0 ? Math.round(d.totalFunded/d.totalTarget*100)+'%' : '—',
        'Targets Met': d.met,
      }))
    } else if (reportType === 'funding') {
      sheetName = 'Wallet Funding Log'
      rows = filteredFunding.map(r => ({
        'Week Ending': r.week_ending, 'Payment Date': r.payment_date||'',
        'Agent ID': r.agent_id_text||'', 'Agent Name': r.agent_name||'',
        'Amount': r.amount_funded || r.amount, 'Revenue Code': r.revenue_code||'', 'Revenue Name': r.revenue_name||'',
      }))
    } else if (reportType === 'infractions') {
      sheetName = 'Infractions Log'
      rows = filteredInfractions.map(r => ({
        'Date': r.date_logged, 'Company Name': r.super_agents?.company_name||'—',
        'Super Agent': r.super_agents?.owner_name||'—', 'LGA': r.super_agents?.lgas?.name||'—',
        'Infraction Types': (r.infraction_types||[]).join(', '),
        'Enforcement Level': r.enforcement_level||'—',
        'Description': r.description||'', 'Notes': r.notes||'',
        'Logged By': r.logged_by||'',
      }))
    }
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
    XLSX.writeFile(wb, `ACSM_${sheetName.replace(/ /g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`)
  }

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading reports…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Reports</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Super Agent · Weekly Agent · By LGA · Wallet Funding · Infractions Log
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select style={selS} value={reportType} onChange={e => setReportType(e.target.value)}>
          {REPORT_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {reportType !== 'infractions' && (
          <select style={selS} value={filterSA} onChange={e => setFilterSA(e.target.value)}>
            <option value="">All Super Agents</option>
            <option value="__all__">Show All Agents</option>
            {superAgents.map(sa => <option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
          </select>
        )}
        {reportType === 'infractions' && (
          <select style={selS} value={filterSA} onChange={e => setFilterSA(e.target.value)}>
            <option value="">All Super Agents</option>
            {superAgents.map(sa => <option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
          </select>
        )}
        <select style={selS} value={filterLga} onChange={e => setFilterLga(e.target.value)}>
          <option value="">All LGAs</option>
          {lgas.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <input style={selS} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <input style={selS} type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)} />
        <button style={{ ...btnPrimary, background:T.surface3, color:T.txtPrimary, border:`1px solid ${T.border}` }}
          onClick={() => { setFilterSA(''); setFilterLga(''); setDateFrom(''); setDateTo('') }}>Clear</button>
        <button style={{ padding:'8px 16px', background:'rgba(63,185,80,.15)', color:'#3fb950',
          border:'1px solid rgba(63,185,80,.25)', borderRadius:6, fontSize:12, fontWeight:700,
          fontFamily:T.font, cursor:'pointer' }} onClick={exportExcel}>⬇ Export Excel</button>
      </div>

      {/* ── SUPER AGENT REPORT ── */}
      {reportType === 'super-agent' && (
        <div style={card}>
          <div style={cardTitle}>Super Agent Report ({filteredSA.length} records)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={tbl}>
              <thead><tr>
                {['Week','Company Name','LGA','Agents','Total Funded','Weekly Target','% of Target','Status'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredSA.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, color:T.txtSecondary, whiteSpace:'nowrap' }}>{r.week_ending}</td>
                    <td style={{ ...td, fontWeight:600 }}>{r.super_agents?.company_name}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.super_agents?.lgas?.name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>
                      {(() => {
                        const live = agents.filter(a => a.super_agent_id === r.super_agent_id).length
                        const stale = live !== r.agent_count
                        return (
                          <span style={{ color: stale ? '#f85149' : 'inherit' }}>
                            {r.agent_count}
                            {stale && <span title={`Current live count: ${live} — re-run scoring to update`}
                              style={{ marginLeft:5, fontSize:9, color:'#f85149', cursor:'help' }}>⚠</span>}
                          </span>
                        )
                      })()}
                    </td>
                    <td style={{ ...td, fontWeight:700, color:rag(r.funding_pct||0).color }}>{fmt(r.total_funded)}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{fmt(r.weekly_target)}</td>
                    <td style={{ ...td, fontWeight:800, color:rag(r.funding_pct||0).color }}>{r.funding_pct}%</td>
                    <td style={td}>
                      <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:9, fontWeight:700,
                        background:rag(r.funding_pct||0).bg,
                        color:rag(r.funding_pct||0).color,
                        border:`1px solid ${rag(r.funding_pct||0).border}` }}>
                        {rag(r.funding_pct||0).label}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredSA.length===0 && <tr><td colSpan={8} style={{ ...td,textAlign:'center',color:T.txtMuted }}>No records</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── WEEKLY AGENT REPORT ── */}
      {reportType === 'weekly' && (
        <div style={card}>
          <div style={cardTitle}>Weekly Agent Report ({agentBoard.length} agents)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={tbl}>
              <thead><tr>
                {['Agent ID','Agent Name','LGA','Company Name','Cl.','Weekly Target','Total Funded','%','Status'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {agentBoard.sort((a,b) => b.pct-a.pct).map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{r.agent_id||'—'}</td>
                    <td style={{ ...td, fontWeight:600 }}>{r.full_name}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.lgas?.name}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.super_agents?.company_name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary, fontSize:10 }}>{r.agent_class}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{fmt(r.baseTarget)}</td>
                    <td style={{ ...td, fontWeight:700, color:rag(r.pct||0).color }}>{fmt(r.funded)}</td>
                    <td style={{ ...td, fontWeight:800, color:rag(r.pct||0).color }}>{r.pct}%</td>
                    <td style={td}>
                      <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:9, fontWeight:700,
                        background:rag(r.pct||0).bg,
                        color:rag(r.pct||0).color,
                        border:`1px solid ${rag(r.pct||0).border}` }}>
                        {rag(r.pct||0).label}
                      </span>
                    </td>
                  </tr>
                ))}
                {agentBoard.length===0 && <tr><td colSpan={9} style={{ ...td,textAlign:'center',color:T.txtMuted }}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── LGA SUMMARY ── */}
      {reportType === 'lga' && (
        <div style={card}>
          <div style={cardTitle}>LGA Summary Report ({Object.keys(lgaSummary).length} LGAs)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={tbl}>
              <thead><tr>
                {['LGA','Super Agents','Total Funded','Total Target','% of Target','Targets Met'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {Object.entries(lgaSummary).sort((a,b) => (b[1].totalFunded/b[1].totalTarget)-(a[1].totalFunded/a[1].totalTarget)).map(([lga,d]) => {
                  const pct = d.totalTarget > 0 ? Math.round(d.totalFunded/d.totalTarget*100) : 0
                  const met = pct >= 100
                  return (
                    <tr key={lga}>
                      <td style={{ ...td, fontWeight:600 }}>{lga}</td>
                      <td style={{ ...td, color:T.txtSecondary }}>{d.count}</td>
                      <td style={{ ...td, fontWeight:700, color:rag(pct).color }}>{fmt(d.totalFunded)}</td>
                      <td style={{ ...td, color:T.txtSecondary }}>{fmt(d.totalTarget)}</td>
                      <td style={{ ...td, fontWeight:800, color:rag(pct).color }}>{pct}%</td>
                      <td style={{ ...td, color:T.txtSecondary }}>{d.met} / {d.count}</td>
                    </tr>
                  )
                })}
                {Object.keys(lgaSummary).length===0 && <tr><td colSpan={6} style={{ ...td,textAlign:'center',color:T.txtMuted }}>No data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── FUNDING LOG ── */}
      {reportType === 'funding' && (
        <div style={card}>
          <div style={cardTitle}>Wallet Funding Log ({filteredFunding.length} records)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={tbl}>
              <thead><tr>
                {['Week Ending','Pay Date','Agent ID','Agent Name','Amount','Revenue Code','Revenue Name'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredFunding.slice(0,500).map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, color:T.txtSecondary, whiteSpace:'nowrap' }}>{r.week_ending}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.payment_date||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{r.agent_id_text||'—'}</td>
                    <td style={td}>{r.agent_name||'—'}</td>
                    <td style={{ ...td, fontWeight:700, color:'#3fb950' }}>{fmt(r.amount_funded || r.amount)}</td>
                    <td style={{ ...td, color:T.txtMuted, fontFamily:'monospace', fontSize:10 }}>{r.revenue_code||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.revenue_name||'—'}</td>
                  </tr>
                ))}
                {filteredFunding.length===0 && <tr><td colSpan={7} style={{ ...td,textAlign:'center',color:T.txtMuted }}>No records</td></tr>}
                {filteredFunding.length>500 && <tr><td colSpan={7} style={{ ...td,textAlign:'center',color:T.txtMuted }}>Showing first 500 — export Excel for full data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── INFRACTIONS LOG ── */}
      {reportType === 'infractions' && (
        <div style={card}>
          <div style={cardTitle}>Weekly Infractions Log ({filteredInfractions.length} records)</div>
          <div style={{ overflowX:'auto' }}>
            <table style={tbl}>
              <thead><tr>
                {['Date','Company Name','Super Agent','LGA','Infraction Types','Level','Logged By'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredInfractions.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, color:T.txtSecondary, whiteSpace:'nowrap' }}>{r.date_logged}</td>
                    <td style={{ ...td, fontWeight:600 }}>{r.super_agents?.company_name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.super_agents?.owner_name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{r.super_agents?.lgas?.name||'—'}</td>
                    <td style={td}>
                      {(r.infraction_types||[]).map(t => (
                        <span key={t} style={{ display:'inline-block', padding:'1px 7px', borderRadius:20,
                          fontSize:9, fontWeight:700, marginRight:3, marginBottom:2,
                          background:'rgba(248,81,73,.12)', color:'#f85149',
                          border:'1px solid rgba(248,81,73,.2)' }}>
                          {t}
                        </span>
                      ))}
                      {r.description && (
                        <div style={{ fontSize:10, color:T.txtSecondary, marginTop:3 }}>{r.description}</div>
                      )}
                    </td>
                    <td style={{ ...td, fontSize:10, fontWeight:600, color:'#f0883e', whiteSpace:'nowrap' }}>
                      {r.enforcement_level||'—'}
                    </td>
                    <td style={{ ...td, fontSize:10, color:T.txtMuted }}>{r.logged_by||'—'}</td>
                  </tr>
                ))}
                {filteredInfractions.length===0 && (
                  <tr><td colSpan={7} style={{ ...td,textAlign:'center',color:T.txtMuted }}>No infractions for this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
